import { LINE_STRING, POLYGON } from 'client/utils/constants/geometryType';
import {
    isPointCoordinate,
    areSamePoint,
    readGeometryCoordinates,
    getExtentExceeded,
    getSketchEdgeStyleVariant
} from '../validationUtils';
import { DRAW_STAGES } from '../drawStageConstants';

const normalizeRing = (geometry) => {
    const coordinates = readGeometryCoordinates(geometry);
    if (!coordinates || !Array.isArray(coordinates[0])) {
        return null;
    }

    const ring = coordinates[0];
    if (ring.length < 5) {
        return null;
    }

    if (!ring.every((point) => isPointCoordinate(point))) {
        return null;
    }

    if (!areSamePoint(ring[0], ring[ring.length - 1])) {
        return null;
    }

    return ring.slice(0, -1);
};

// Area-box mode is intentionally strict: the ring must represent exactly
// the four corners of an axis-aligned rectangle.
const isValidAreaBoxPolygon = (geometry) => {
    const ringWithoutClosingPoint = normalizeRing(geometry);
    if (!ringWithoutClosingPoint || ringWithoutClosingPoint.length !== 4) {
        return false;
    }

    const xs = [...new Set(ringWithoutClosingPoint.map(([x]) => x))];
    const ys = [...new Set(ringWithoutClosingPoint.map(([, y]) => y))];
    if (xs.length !== 2 || ys.length !== 2) {
        return false;
    }

    const combinations = new Set(ringWithoutClosingPoint.map(([x, y]) => `${x}:${y}`));
    if (combinations.size !== 4) {
        return false;
    }

    return xs.every((x) => ys.every((y) => combinations.has(`${x}:${y}`)));
};

const areaBoxPolicy = Object.freeze({
    usesPolygonSketchCache: true,

    getStage ({ geometryType, geometry }) {
        if (geometryType === POLYGON) {
            return isValidAreaBoxPolygon(geometry)
                ? DRAW_STAGES.SHAPE_SKETCH_COMPLETE
                : DRAW_STAGES.SHAPE_SKETCH_INCOMPLETE;
        }

        return DRAW_STAGES.UNKNOWN;
    },

    isComplete ({ geometryType, geometry }) {
        return geometryType === POLYGON && isValidAreaBoxPolygon(geometry);
    },

    shouldNotify ({ geometryType, complete }) {
        if (!complete) {
            return false;
        }

        if (geometryType === POLYGON) {
            return true;
        }

        return false;
    },

    getExtentMessageDescriptor () {
        return {
            id: 'mapPage.error.areaTooLarge',
            values: undefined
        };
    },

    getExtentExceeded (context) {
        return getExtentExceeded(context);
    },

    getStyleVariant (context) {
        return getSketchEdgeStyleVariant(context);
    },

    shouldSkipExtentCheck ({ geometryType, isSketchEdgeGeometry }) {
        // For box/custom polygon modes, OL also emits sketch edges as LineString.
        // Extent checks should be driven by the polygon intent, not temporary edges.
        return geometryType === LINE_STRING && isSketchEdgeGeometry;
    },

    shouldValidateGeometry ({ complete }) {
        return Boolean(complete);
    }
});

export default areaBoxPolicy;

// Exported for targeted unit testing of complex helpers
export { normalizeRing, isValidAreaBoxPolygon };
